'use client';

import { c as _c } from "react/compiler-runtime";
import { FileCode2 } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import type { AiContext } from './types/ai-context';
import { resolveTopVisiblePrV2ChatFilename } from './pr-v2-visible-file';
import { useSidebar } from '@/components/ui/sidebar';
import { BetterTooltip } from '@/components/ui/tooltip';
import { splitFilePath } from '@/lib/file-paths/split-file-path';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';
import { usePrTabNavigationStore } from '@/lib/stores/pr-tab-navigation/tab-navigation-store';
import { cn } from '@/lib/utils';
export function PrV2VisibleFileIndicator(t0) {
  const $ = _c(33);
  const {
    aiContext,
    className
  } = t0;
  const pathname = usePathname();
  const {
    isChangesTab
  } = usePrTabNavigationStore();
  const {
    isMobile,
    open,
    openMobile
  } = useSidebar();
  const isSidebarOpen = isMobile ? openMobile : open;
  let t1;
  if ($[0] !== isChangesTab || $[1] !== isSidebarOpen || $[2] !== pathname) {
    t1 = isPrV2Route(pathname) && isChangesTab && isSidebarOpen;
    $[0] = isChangesTab;
    $[1] = isSidebarOpen;
    $[2] = pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shouldTrackVisibleFile = t1;
  const [visibleFilename, setVisibleFilename] = useState(null);
  const updateFrameRef = useRef(null);
  let t2;
  if ($[4] !== aiContext) {
    t2 = () => {
      setVisibleFilename(resolveTopVisiblePrV2ChatFilename(aiContext));
    };
    $[4] = aiContext;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const updateVisibleFilename = useEffectEvent(t2);
  let t3;
  if ($[6] !== updateVisibleFilename) {
    t3 = () => {
      if (updateFrameRef.current != null) {
        return;
      }
      if (typeof window.requestAnimationFrame !== "function") {
        updateVisibleFilename();
        return;
      }
      updateFrameRef.current = window.requestAnimationFrame(() => {
        updateFrameRef.current = null;
        updateVisibleFilename();
      });
    };
    $[6] = updateVisibleFilename;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const scheduleVisibleFilenameUpdate = useEffectEvent(t3);
  let t4;
  if ($[8] !== shouldTrackVisibleFile || $[9] !== updateVisibleFilename) {
    t4 = () => {
      if (!shouldTrackVisibleFile) {
        setVisibleFilename(null);
        return;
      }
      updateVisibleFilename();
    };
    $[8] = shouldTrackVisibleFile;
    $[9] = updateVisibleFilename;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== aiContext || $[12] !== shouldTrackVisibleFile) {
    t5 = [aiContext, shouldTrackVisibleFile];
    $[11] = aiContext;
    $[12] = shouldTrackVisibleFile;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== scheduleVisibleFilenameUpdate || $[15] !== shouldTrackVisibleFile) {
    t6 = () => {
      if (!shouldTrackVisibleFile) {
        return;
      }
      const scrollContainer = document.getElementById("scrollable-content");
      const scrollTarget = scrollContainer ?? window;
      scrollTarget.addEventListener("scroll", scheduleVisibleFilenameUpdate, {
        passive: true
      });
      window.addEventListener("resize", scheduleVisibleFilenameUpdate);
      return () => {
        scrollTarget.removeEventListener("scroll", scheduleVisibleFilenameUpdate);
        window.removeEventListener("resize", scheduleVisibleFilenameUpdate);
        if (updateFrameRef.current != null) {
          window.cancelAnimationFrame(updateFrameRef.current);
          updateFrameRef.current = null;
        }
      };
    };
    $[14] = scheduleVisibleFilenameUpdate;
    $[15] = shouldTrackVisibleFile;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== shouldTrackVisibleFile) {
    t7 = [shouldTrackVisibleFile];
    $[17] = shouldTrackVisibleFile;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  useEffect(t6, t7);
  if (!shouldTrackVisibleFile || !visibleFilename) {
    return null;
  }
  let t8;
  if ($[19] !== visibleFilename) {
    t8 = splitFilePath(visibleFilename);
    $[19] = visibleFilename;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const {
    basename
  } = t8;
  const t9 = `Looking at ${visibleFilename}`;
  let t10;
  if ($[21] !== className) {
    t10 = cn("flex min-w-0 max-w-full items-center gap-1.5 text-xs leading-4 text-zinc-500", className);
    $[21] = className;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <FileCode2 aria-hidden={true} className="size-3 shrink-0" />;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== basename) {
    t12 = <span className="min-w-0 truncate">{basename}</span>;
    $[24] = basename;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t9) {
    t13 = <div data-testid="chat-visible-file-indicator" aria-label={t9} className={t10}>{t11}{t12}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== visibleFilename) {
    t14 = <BetterTooltip content={visibleFilename} side="top" align="start">{t13}</BetterTooltip>;
    $[30] = t13;
    $[31] = visibleFilename;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}